import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button';
import { useDuplicateMenuProduct } from '../logic/use-duplicate-menu-product';

interface ProductMenuDuplicateProps {
  productId: string;
}

export default function ProductMenuDuplicate({ productId }: ProductMenuDuplicateProps) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [duplicateMenuProduct, { loading }] = useDuplicateMenuProduct();

  const handleDuplicate = () => {
    dialog.confirmAlert({
      title: t('menu.menu-detail.product.edit.extra-action.duplicate.confirm.title', 'Duplicate Menu Product'),
      message: t(
        'menu.menu-detail.product.edit.extra-action.duplicate.confirm.message',
        'Are you sure you want to duplicate this menu product?',
      ),
      textButton: t('menu.menu-detail.product.edit.extra-action.duplicate.confirm.button', 'Duplicate'),
      onConfirm: async () => {
        try {
          const result = await duplicateMenuProduct({ variables: { id: productId } });
          showAlert({
            status: 'success',
            title: t('common.success'),
            message: t(
              'menu.menu-detail.product.edit.extra-action.duplicate.success',
              'Menu product duplicated successfully!',
            ),
          });

          // Navigate to the newly created product
          const duplicatedProductId = result.data?.duplicateMenuProduct.id;
          if (duplicatedProductId) {
            navigate(`../${duplicatedProductId}`);
          }
        } catch (error) {
          showAlert({
            status: 'error',
            title: t('common.error'),
            message: t(
              'menu.menu-detail.product.edit.extra-action.duplicate.error',
              'Failed to duplicate menu product!',
            ),
          });
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          {t('menu.menu-detail.product.edit.extra-action.duplicate.title', 'Duplicate Product')}
        </h3>
        <p className="text-sm text-gray-600">
          {t(
            'menu.menu-detail.product.edit.extra-action.duplicate.description',
            'Create a copy of this menu product with all its settings.',
          )}
        </p>
      </div>
      <SecondaryButton onClick={handleDuplicate} disabled={loading}>
        {t('menu.menu-detail.product.edit.extra-action.duplicate.button', 'Duplicate Product')}
      </SecondaryButton>
    </div>
  );
}
